import { ELiveRecordFilter } from '@constants';
import { createQueryString } from '@utils';

import { IComment } from '../comments';
import { IPaginatedResponse, IPagination, ISorter } from '../common';
import { IFile } from '../file';
import { IMessage } from '../messages';
import { RequestService } from '../request';
import { orderQuery } from '../ticket/utils';

import {
  IAddEventAttachment,
  IAddEventLiveRecord,
  IAddLiveRecordView,
  IAddRemoveAttendee,
  IBasicEvent,
  IChangeEventCommentUsefulVote,
  IChangeEventMessageUsefulVote,
  ICreateEvent,
  IEvent,
  IEventByType,
  IEventsFilters,
  IGroupedLiveRecords,
  IStudentLiveClassesStats,
  ILiveClassStat,
  IRemoveEventAttachment,
  IRemoveEventComment,
  IRemoveEventLiveRecord,
  IRemoveEventMessage,
  ISendComment,
  ISendEventMessage,
  IUpdateEvent,
  IUpdateEventLiveRecordName,
  IBulkEditLiveClassFormations,
  IRemoveEventsResult
} from './event.contract';

export class EventService extends RequestService {
  constructor(apiUrl: string) {
    super(apiUrl);
  }

  public getAllEvents(filters: IEventsFilters) {
    const query = createQueryString({ filters });
    return this.fetcher<IEventByType[]>(`/events?${query}`);
  }

  public getEvent(eventId: string) {
    return this.fetcher<IEvent>(`/events/${eventId}`);
  }

  public getEventsWidgetNextEvents() {
    return this.fetcher<IEvent[]>('/events/widget/next-events');
  }

  public createEvent(newEvent: ICreateEvent) {
    return this.poster<IEvent, ICreateEvent>('/events', newEvent);
  }

  public updateEvent(updatedEvent: IUpdateEvent) {
    return this.putter<IEvent, IUpdateEvent>(`/events/${updatedEvent._id}`, updatedEvent);
  }

  public addAttendee({ eventId, attendeeId }: IAddRemoveAttendee) {
    return this.putter<IEvent, undefined>(`/events/${eventId}/attendees/${attendeeId}`);
  }

  public removeAttendee({ eventId, attendeeId }: IAddRemoveAttendee) {
    return this.deleter<IEvent>(`/events/${eventId}/attendees/${attendeeId}`);
  }

  public removeEvent(eventId: string, removeRecurring: boolean) {
    const query = createQueryString({ filters: removeRecurring ? { removeRecurring } : {} });
    return this.deleter<IRemoveEventsResult>(`/events/${eventId}?${query}`);
  }

  public sendMessage({ commentId, message, attached }: ISendEventMessage) {
    return this.poster(`/events/comments/${commentId}/messages`, { content: message, attached });
  }

  public removeMessage({ commentId, messageId }: IRemoveEventMessage) {
    return this.deleter<IMessage>(`/events/comments/${commentId}/messages/${messageId}`);
  }

  public changeUsefulMessageVote({ messageId, userId }: IChangeEventMessageUsefulVote) {
    return this.putter(`/events/messages/${messageId}/useful-answer/${userId}`);
  }
  public changeUsefulCommentVote({ commentId, userId }: IChangeEventCommentUsefulVote) {
    return this.putter(`/events/comments/${commentId}/useful-answer/${userId}`);
  }

  public removeAttachment({ eventId, attachmentId }: IRemoveEventAttachment) {
    return this.deleter(`/events/${eventId}/attachments/${attachmentId}`);
  }

  public sendComment({ event, comment, attached }: ISendComment) {
    return this.poster<IComment, { content: string; attached?: IFile; event?: string }>(
      `/events/${event}/comments`,
      { content: comment, attached, event }
    );
  }

  public removeComment({ eventId, commentId }: IRemoveEventComment) {
    return this.deleter(`/events/${eventId}/comments/${commentId}`);
  }

  public addAttachment({ eventId, file }: IAddEventAttachment) {
    return this.poster(`/events/${eventId}/attachments`, { attachment: file });
  }

  public removeLiveRecord({ eventId, liveRecordId }: IRemoveEventLiveRecord) {
    return this.deleter(`/events/${eventId}/live-records/${liveRecordId}`);
  }

  public addLiveRecord({ eventId, file }: IAddEventLiveRecord) {
    return this.poster(`/events/${eventId}/live-records`, { liveRecord: file });
  }

  public removeEventsInBulk(ids: string[]) {
    return this.poster<IRemoveEventsResult, { eventsIds: string[] }>(
      `/events/bulk-delete`,
      { eventsIds: ids }
    );
  }

  public editLiveClassInBulk(data: IBulkEditLiveClassFormations) {
    return this.poster(`/events/bulk-edit-formations`, data);
  }

  public getTodayEvents() {
    return this.fetcher<IBasicEvent[]>('/events/today');
  }

  public findAllStudentLiveRecordsByCourse(courseId: string, filter: ELiveRecordFilter) {
    const query = createQueryString({ filters: { filter } });
    return this.fetcher<IGroupedLiveRecords[]>(`/events/live-records/students/${courseId}?${query}`);
  }

  public findCourseLiveRecords(courseId: string) {
    return this.fetcher<IGroupedLiveRecords[]>(`/events/live-records/${courseId}`);
  }

  public getCourseLiveRecords(courseId: string, filter?: ELiveRecordFilter) {
    const query = filter ? createQueryString({ filters: { filter } }) : '';
    return this.fetcher<IGroupedLiveRecords[]>(
      `/events/course-live-records/${courseId}${query ? `?${query}` : ''}`
    );
  }

  public getStudentLiveClassesStats() {
    return this.fetcher<IStudentLiveClassesStats>(`/events/live-classes/student-stats`);
  }

  public updateLiveRecordName({ eventId, liveRecordId, newName }: IUpdateEventLiveRecordName) {
    return this.putter(`/events/${eventId}/live-records/${liveRecordId}/name`, { newName });
  }

  public getLiveClass(meetingId: string) {
    return this.fetcher<{ roomUrl: string; event: IEvent }>(`/events/live-class/${meetingId}`);
  }

  public findAllCommentsByEventId(eventId: string, pagination?: IPagination, sort?: ISorter) {
    const query = createQueryString({
      limit: pagination?.limit,
      page: pagination?.page,
      sort:
        sort?.order && sort?.field
          ? orderQuery(sort.order, {
              sort: Array.isArray(sort.field) ? sort.field.join('.') : sort.field.toString()
            })
          : undefined
    });
    return this.fetcher<IPaginatedResponse<IComment>>(`/events/${eventId}/comments?${query}`);
  }

  public addLiveRecordView(add: IAddLiveRecordView) {
    return this.putter(`/events/${add.eventId}/live-records/${add.liveRecordId}/views`);
  }

  public findAllLiveClassesStats(
    pagination: IPagination,
    filters?: IEventsFilters,
    sort?: ISorter
  ) {
    const query = createQueryString({
      limit: pagination.limit,
      page: pagination.page,
      filters,
      sort: sort?.field
        ? orderQuery(sort?.order ?? 'ascend', { sort: String(sort.field) })
        : undefined
    });
    return this.fetcher<IPaginatedResponse<ILiveClassStat>>(`/events/live-classes/stats?${query}`);
  }
}
